/**
 * Hook for managing Brazil EOL interpreter selection state,
 * version modal, and search/filter for the Brazil EOL view.
 */
import { useState, useCallback } from 'react';

export const useBrazilEOLSelection = () => {
  const [brazilEOLRuntimeFilter, setBrazilEOLRuntimeFilter] = useState<'cpython' | 'nodejs'>('cpython');
  const [brazilEOLSearchQuery, setBrazilEOLSearchQuery] = useState('');
  const [selectedBrazilPipelines, setSelectedBrazilPipelines] = useState<Set<string>>(new Set());
  const [isBrazilVersionModalOpen, setIsBrazilVersionModalOpen] = useState(false);
  const [selectedBrazilVersions, setSelectedBrazilVersions] = useState<{ cpython?: string; nodejs?: string }>({});

  const handleBrazilEOLSearchFilterChange = useCallback((text: string) => {
    setBrazilEOLSearchQuery(text);
  }, []);

  const toggleBrazilPipelineSelection = useCallback((pipelineName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedBrazilPipelines((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(pipelineName)) {
        next.delete(pipelineName);
      } else {
        next.add(pipelineName);
      }
      return next;
    });
  }, []);

  const isBrazilPipelineSelected = useCallback((pipelineName: string) => selectedBrazilPipelines.has(pipelineName), [selectedBrazilPipelines]);

  const clearBrazilSelection = useCallback(() => {
    setSelectedBrazilPipelines(new Set());
  }, []);

  /** Open version modal with default recommended versions. */
  const openBrazilVersionModal = useCallback(() => {
    setSelectedBrazilVersions({
      cpython: 'CPython310',
      nodejs: 'NodeJS-20.x'
    });
    setIsBrazilVersionModalOpen(true);
  }, []);

  return {
    brazilEOLRuntimeFilter,
    setBrazilEOLRuntimeFilter,
    brazilEOLSearchQuery,
    handleBrazilEOLSearchFilterChange,
    selectedBrazilPipelines,
    setSelectedBrazilPipelines,
    toggleBrazilPipelineSelection,
    isBrazilPipelineSelected,
    clearBrazilSelection,
    isBrazilVersionModalOpen,
    setIsBrazilVersionModalOpen,
    selectedBrazilVersions,
    setSelectedBrazilVersions,
    openBrazilVersionModal
  };
};
